import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
  CircularProgress,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  Divider,
  InputAdornment,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { appReleaseService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import {
  copyDownloadUrl,
  openApkDownload,
} from '../utils/appReleaseDownload'
import { formatDateTime } from '../utils/dateFormat'

const formatBytes = (bytes) => {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

const ReleaseDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.APP_RELEASE,
    'write',
  )

  const [release, setRelease] = useState(null)
  const [activeRelease, setActiveRelease] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [activateOpen, setActivateOpen] = useState(false)
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const [editOpen, setEditOpen] = useState(false)
  const [editForm, setEditForm] = useState({ update_type: 'SOFT', changelog: '' })
  const [editError, setEditError] = useState(null)

  const [menuAnchor, setMenuAnchor] = useState(null)
  const [toast, setToast] = useState('')
  const [downloadInfo, setDownloadInfo] = useState(null)
  const [downloadLoading, setDownloadLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await appReleaseService.getById(id)
      setRelease(r)
      // Fetch active release for the activate confirmation modal
      if (!r.is_active) {
        const list = await appReleaseService.list({ page: 1, limit: 100 })
        setActiveRelease(list.items?.find((x) => x.is_active) || null)
      } else {
        setActiveRelease(null)
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load release')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const handleActivate = async () => {
    setActionLoading(true)
    try {
      await appReleaseService.activate(id)
      setActivateOpen(false)
      setToast('Release activated')
      await load()
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Activate failed')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeactivate = async () => {
    setActionLoading(true)
    try {
      await appReleaseService.deactivate(id)
      setDeactivateOpen(false)
      setToast('Release deactivated')
      await load()
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Deactivate failed')
    } finally {
      setActionLoading(false)
    }
  }

  const openEdit = () => {
    setEditForm({
      update_type: release.update_type,
      changelog: release.changelog || '',
    })
    setEditError(null)
    setEditOpen(true)
  }

  const handleSaveEdit = async () => {
    setEditError(null)
    setActionLoading(true)
    try {
      await appReleaseService.update(id, {
        update_type: editForm.update_type,
        changelog: editForm.changelog,
      })
      setEditOpen(false)
      setToast('Release updated')
      await load()
    } catch (e) {
      setEditError(
        e.response?.data?.message || e.message || 'Update failed',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const copyChecksum = () => {
    if (release?.sha256_checksum) {
      navigator.clipboard.writeText(release.sha256_checksum)
      setToast('SHA-256 copied')
    }
  }

  const fetchDownloadUrl = async () => {
    setDownloadLoading(true)
    try {
      const data = await appReleaseService.getDownloadUrl(id)
      setDownloadInfo(data)
      return data
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to get download link')
      return null
    } finally {
      setDownloadLoading(false)
    }
  }

  const handleDownloadApk = async () => {
    const data = downloadInfo?.download_url ? downloadInfo : await fetchDownloadUrl()
    if (!data?.download_url) return
    openApkDownload(data.download_url)
    setToast(`Download started for ${release.version_name}`)
  }

  const handleCopyDownloadLink = async () => {
    const data = downloadInfo?.download_url ? downloadInfo : await fetchDownloadUrl()
    if (!data?.download_url) return
    await copyDownloadUrl(data.download_url)
    setToast('Download link copied to clipboard')
  }

  const handleCopyDisplayedLink = async () => {
    if (!downloadInfo?.download_url) return
    await copyDownloadUrl(downloadInfo.download_url)
    setToast('Download link copied to clipboard')
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!release) {
    return (
      <Alert severity="error">{error || 'Release not found'}</Alert>
    )
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/releases')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Release {release.version_name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canWrite && !release.is_active && (
            <Button
              variant="contained"
              onClick={() => setActivateOpen(true)}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
            >
              Activate
            </Button>
          )}
          {canWrite && (
            <Button variant="outlined" onClick={openEdit}>
              Edit
            </Button>
          )}
          {canWrite && release.is_active && (
            <>
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={() => setMenuAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null)
                    setDeactivateOpen(true)
                  }}
                  sx={{ color: 'warning.main' }}
                >
                  Deactivate (kill switch)
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, rowGap: 1.5, columnGap: 2 }}>
            <Typography color="text.secondary">Status</Typography>
            <Box>
              {release.is_active ? (
                <Chip label="Active" size="small" color="success" />
              ) : (
                <Chip label="Inactive" size="small" />
              )}
            </Box>

            <Typography color="text.secondary">versionCode</Typography>
            <Typography>{release.version_code}</Typography>

            <Typography color="text.secondary">Update Type</Typography>
            <Box>
              <Chip
                label={release.update_type}
                size="small"
                color={release.update_type === 'FORCE' ? 'warning' : 'default'}
              />
            </Box>

            <Typography color="text.secondary">File Size</Typography>
            <Typography>{formatBytes(release.file_size_bytes)}</Typography>

            <Typography color="text.secondary">SHA-256</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                {release.sha256_checksum}
              </Typography>
              <IconButton size="small" onClick={copyChecksum} sx={{ ml: 1 }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography color="text.secondary">S3 Key</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
              {release.s3_key}
            </Typography>

            <Typography color="text.secondary">Created</Typography>
            <Typography>
              {formatDateTime(release.created_at)} by {release.created_by_admin_email || `#${release.created_by_admin_id}`}
            </Typography>

            {release.last_activated_at && (
              <>
                <Typography color="text.secondary">Last Activated</Typography>
                <Typography>
                  {formatDateTime(release.last_activated_at)} by {release.last_activated_by_admin_id ? `#${release.last_activated_by_admin_id}` : '—'}
                </Typography>
              </>
            )}

            {release.last_deactivated_at && (
              <>
                <Typography color="text.secondary">Last Deactivated</Typography>
                <Typography>
                  {formatDateTime(release.last_deactivated_at)} by {release.last_deactivated_by_admin_id ? `#${release.last_deactivated_by_admin_id}` : '—'}
                </Typography>
              </>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            APK Download
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={downloadLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadApk}
              disabled={downloadLoading}
              sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}
            >
              Download APK
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyDownloadLink}
              disabled={downloadLoading}
            >
              Copy download link
            </Button>
          </Box>
          {downloadInfo?.download_url && (
            <>
              <TextField
                label="Download link"
                value={downloadInfo.download_url}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopyDisplayedLink} edge="end" aria-label="Copy download link">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Permanent public S3 link for this APK.
              </Typography>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Changelog
          </Typography>
          <Box
            sx={{
              bgcolor: '#fafafa',
              borderRadius: 1,
              p: 2,
              whiteSpace: 'pre-wrap',
              fontSize: '0.9rem',
              minHeight: 60,
            }}
          >
            {release.changelog || <em style={{ color: '#999' }}>No changelog provided.</em>}
          </Box>
        </CardContent>
      </Card>

      {/* Activate confirmation */}
      <Dialog open={activateOpen} onClose={() => setActivateOpen(false)}>
        <DialogTitle>Activate version {release.version_name}?</DialogTitle>
        <DialogContent>
          {activeRelease ? (
            <DialogContentText>
              This will deactivate the currently active release:{' '}
              <b>
                {activeRelease.version_name} (versionCode {activeRelease.version_code})
              </b>
              . All mobile clients on a lower versionCode will see the update prompt on their
              next cold start.
              <br />
              <br />
              <Alert severity="warning" sx={{ mt: 1 }}>
                <b>Note on rollback:</b> Android refuses to install an APK with a lower
                versionCode over an installed app. If this is an older release, clients on
                the broken newer version will not auto-downgrade — they would need to
                uninstall first or wait for a new release with versionCode &gt; broken.
              </Alert>
            </DialogContentText>
          ) : (
            <DialogContentText>
              No release is currently active. Activating this one will start serving update
              prompts to mobile clients on lower versionCodes.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleActivate}
            disabled={actionLoading}
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)}>
        <DialogTitle>Deactivate version {release.version_name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            After deactivation there will be <b>no active release</b>. Mobile clients will
            stop receiving update prompts until you activate another release.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleDeactivate}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit metadata */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Release Metadata</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <FormControl sx={{ mt: 1, mb: 2 }}>
            <FormLabel sx={{ mb: 1 }}>Update Type</FormLabel>
            <RadioGroup
              row
              value={editForm.update_type || 'SOFT'}
              onChange={(e) => setEditForm((f) => ({ ...f, update_type: e.target.value }))}
            >
              <FormControlLabel value="SOFT" control={<Radio />} label="Soft" />
              <FormControlLabel value="FORCE" control={<Radio />} label="Force" />
            </RadioGroup>
          </FormControl>
          <TextField
            label="Changelog"
            value={editForm.changelog || ''}
            onChange={(e) => setEditForm((f) => ({ ...f, changelog: e.target.value }))}
            multiline
            rows={5}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Box>
  )
}

export default ReleaseDetailPage
